import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../../components/feature/AdminLayout';
import EmergencyPrintDropdown from '../../../components/feature/EmergencyPrintDropdown';
import { supabase } from '../../../services/supabaseClient';
import {
  calcRentalDays,
  calcOrderCost,
  formatCurrency,
  normalizeOrderItems,
  getOrderStatusDisplay,
  type OrderItemData,
} from '../../../modules/order';
import { adminOrderDetailPath } from '../../../utils/orderRoutes';

interface Order {
  id: string;
  order_number: string;
  customer_id: string;
  start_date: string;
  end_date: string;
  pickup_location_id: string;
  return_location_id: string;
  total_price: number;
  total_deposit: number;
  total_amount: number;
  status: string;
  notes: string;
  created_at: string;
  customers?: {
    name: string;
    email: string;
    phone: string;
    discount?: number;
  };
  pickup_location?: {
    name_zh: string;
    name_ja: string;
  };
  return_location?: {
    name_zh: string;
    name_ja: string;
  };
  order_items?: any[];
}

type FilterType = 'all' | 'today' | 'pending' | 'renting' | 'completed' | 'problem';

export default function AdminOrdersPage() {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [currentPage, setCurrentPage] = useState(1);

  const PAGE_SIZE = 25;

  // 從 URL 取得客戶 ID 篩選
  const customerIdFilter = searchParams.get('customerId');
  const [filterCustomerName, setFilterCustomerName] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('orders')
          .select(`
            id,
            order_number,
            status,
            start_date,
            end_date,
            total_price,
            total_deposit,
            created_at,
            customer_id,
            customers (
              id,
              name,
              email,
              phone,
              company
            ),
            pickup_location:locations!orders_pickup_location_id_fkey (
              id,
              name_zh,
              name_ja
            ),
            return_location:locations!orders_return_location_id_fkey (
              id,
              name_zh,
              name_ja
            ),
            order_items (
              id,
              product_id,
              quantity,
              daily_price_snapshot,
              deposit_snapshot,
              subtotal
            )
          `)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('❌ 載入訂單失敗:', error);
          throw error;
        }
        
        console.log('✅ 成功載入訂單:', data);
        setOrders(data || []);
        
        // 如果有客戶 ID 篩選，取得客戶名稱
        if (customerIdFilter && data) {
          const customerOrder = data.find(o => o.customer_id === customerIdFilter);
          if (customerOrder?.customers?.name) {
            setFilterCustomerName(customerOrder.customers.name);
          }
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [customerIdFilter]);

  const getStatusBadgeClass = (status: string) => {
    const statusMap: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      need_info: 'bg-orange-100 text-orange-800',
      approved: 'bg-blue-100 text-blue-800',
      renting: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return statusMap[status] || 'bg-gray-100 text-gray-800';
  };

  // Phase1078A: state phase badge for list
  const getStatePhaseBadge = (status: string, endDate: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    const overdue = status === 'renting' && today > end;

    if (overdue) {
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700 border border-red-200"><i className="ri-alarm-warning-line text-xs"></i>逆期</span>;
    }
    if (['draft', 'pending', 'need_info', 'approved'].includes(status)) {
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-50 text-slate-600 border border-slate-200">契約前</span>;
    }
    if (status === 'renting') {
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">生效中</span>;
    }
    if (['completed', 'cancelled', 'rejected'].includes(status)) {
      return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">已結案</span>;
    }
    return null;
  };

  const getStatusColor = (status: string) => {
    const statusMap: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      need_info: 'bg-orange-100 text-orange-800',
      approved: 'bg-blue-100 text-blue-800',
      renting: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return statusMap[status] || 'bg-gray-100 text-gray-800';
  };

  // Phase1104: use canonical display helper for semantic labels
  const getStatusText = (status: string) => getOrderStatusDisplay(status).adminLabel;

  const handleViewOrder = (orderId: string) => {
    const path = adminOrderDetailPath(orderId);
    if (path) {
      navigate(path);
    }
  };

  const handleCardClick = (filter: FilterType) => {
    setActiveFilter(filter);
    setStatusFilter('all');
    setCurrentPage(1);
  };

  const getOrderCost = (order: Order) => {
    if (!order.order_items || order.order_items.length === 0) {
      return {
        subtotal: order.total_price || 0,
        deposit: order.total_deposit || 0,
        finalTotal: order.total_price || 0
      };
    }

    const days = calcRentalDays(order.start_date, order.end_date);
    const normalizedItems = normalizeOrderItems(order.order_items as OrderItemData[]);
    const discount = order.customers?.discount || 0;
    
    return calcOrderCost(normalizedItems, days, discount);
  };

  const getTodayOrders = () => {
    const today = new Date().toISOString().split('T')[0];
    return orders.filter(order => order.created_at.startsWith(today));
  };

  const getPendingOrders = () => {
    return orders.filter(order => order.status === 'pending');
  };

  const getRentingOrders = () => {
    return orders.filter(order => order.status === 'renting');
  };

  const getCompletedOrders = () => {
    return orders.filter(order => order.status === 'completed');
  };

  const getProblemOrders = () => {
    return orders.filter(order => 
      order.status === 'cancelled' || 
      order.notes?.toLowerCase().includes('缺件') ||
      order.notes?.toLowerCase().includes('損壞') ||
      order.notes?.toLowerCase().includes('問題')
    );
  };

  const filteredOrders = orders.filter(order => {
    // 客戶 ID 篩選（來自 URL 參數）
    if (customerIdFilter && order.customer_id !== customerIdFilter) {
      return false;
    }
    
    // 搜尋篩選
    const matchesSearch = 
      order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customers?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customers?.email.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    // 卡片篩選優先
    if (activeFilter === 'today') {
      const today = new Date().toISOString().split('T')[0];
      return order.created_at.startsWith(today);
    }

    if (activeFilter === 'pending') {
      return order.status === 'pending';
    }

    if (activeFilter === 'renting') {
      return order.status === 'renting';
    }

    if (activeFilter === 'completed') {
      return order.status === 'completed';
    }

    if (activeFilter === 'problem') {
      return order.status === 'cancelled' || 
        order.notes?.toLowerCase().includes('缺件') ||
        order.notes?.toLowerCase().includes('損壞') ||
        order.notes?.toLowerCase().includes('問題');
    }

    // 狀態下拉選單篩選
    if (statusFilter !== 'all') {
      return order.status === statusFilter;
    }

    return true;
  });

  // 分頁邏輯
  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  if (safePage !== currentPage && currentPage > 1) {
    setCurrentPage(safePage);
  }
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const endIdx = startIdx + PAGE_SIZE;
  const pagedOrders = filteredOrders.slice(startIdx, endIdx);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (safePage > 3) pages.push('...');
      const start = Math.max(2, safePage - 1);
      const end = Math.min(totalPages - 1, safePage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (safePage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const stats = [
    {
      id: 'today',
      label: '今日訂單',
      value: getTodayOrders().length,
      icon: 'ri-calendar-line',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      description: '今天新增的訂單'
    },
    {
      id: 'pending',
      label: '等待處理',
      value: getPendingOrders().length,
      icon: 'ri-time-line',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      description: '待確認的訂單'
    },
    {
      id: 'renting',
      label: '出租中',
      value: getRentingOrders().length,
      icon: 'ri-shopping-bag-line',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      description: '已交接、正在租借中'
    },
    {
      id: 'completed',
      label: '已歸還',
      value: getCompletedOrders().length,
      icon: 'ri-checkbox-circle-line',
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      description: '已完成的訂單'
    },
    {
      id: 'problem',
      label: '爭議訂單',
      value: getProblemOrders().length,
      icon: 'ri-alert-line',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      description: '缺件、損壞或逾期'
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-gray-600">載入中...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="px-6 py-8">
        {/* 標題區 */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            {customerIdFilter && (
              <button
                onClick={() => {
                  setSearchParams({});
                  setFilterCustomerName(null);
                }}
                className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                <span className="text-sm">返回全部訂單</span>
              </button>
            )}
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {customerIdFilter && filterCustomerName 
              ? `${filterCustomerName} 的訂單` 
              : '訂單管理'
            }
          </h1>
          <p className="text-gray-600">
            {customerIdFilter 
              ? `查看此客戶的所有租借訂單（共 ${filteredOrders.length} 筆）`
              : '管理所有租借訂單'
            }
          </p>
        </div>

        {/* 客戶篩選提示條 */}
        {customerIdFilter && filterCustomerName && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <i className="ri-user-line text-blue-600 text-lg"></i>
              </div>
              <div>
                <p className="text-sm font-medium text-blue-900">
                  正在篩選：{filterCustomerName}
                </p>
                <p className="text-xs text-blue-600">
                  顯示此客戶的所有訂單記錄
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setSearchParams({});
                setFilterCustomerName(null);
              }}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-close-line"></i>
              清除篩選
            </button>
          </div>
        )}

        {/* 統計卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          {stats.map((stat) => (
            <button
              key={stat.id}
              onClick={() => handleCardClick(stat.id as FilterType)}
              className={`bg-white rounded-lg p-6 border-2 transition-all duration-200 cursor-pointer hover:shadow-lg hover:scale-105 ${
                activeFilter === stat.id 
                  ? 'border-blue-500 shadow-md' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 ${stat.bgColor} rounded-lg flex items-center justify-center`}>
                  <i className={`${stat.icon} text-2xl ${stat.color}`}></i>
                </div>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
              <div className="text-sm font-medium text-gray-900 mb-1">{stat.label}</div>
              <div className="text-xs text-gray-500">{stat.description}</div>
            </button>
          ))}
        </div>

        {/* 搜尋與篩選區 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
            <div className="flex-1 w-full lg:w-auto">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                <input
                  type="text"
                  placeholder="搜尋訂單編號、客戶姓名或電子郵件..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 w-full lg:w-auto">
              <div className="w-full lg:w-48">
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setActiveFilter('all');
                    setCurrentPage(1);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="all">所有狀態</option>
                  <option value="pending">待確認</option>
                  <option value="approved">已確認</option>
                  <option value="renting">出租中</option>
                  <option value="need_info">需補充資訊</option>
                  <option value="completed">已完成</option>
                  <option value="cancelled">已取消</option>
                </select>
              </div>
              <EmergencyPrintDropdown
                onSelectType={(type) => {
                  console.log('🖨️ Emergency print selected:', type);
                }}
              />
            </div>
          </div>
        </div>

        {/* 訂單列表 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    訂單編號
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    客戶資訊
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    租借期間
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    取還件門市
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    金額
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    狀態
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pagedOrders.map((order) => {
                  const cost = getOrderCost(order);
                  return (
                    <tr key={order.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewOrder(order.id)}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {order.order_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{order.customers?.name || '-'}</div>
                        <div className="text-xs text-gray-500">{order.customers?.email || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {order.start_date} ~ {order.end_date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          取：{order.pickup_location?.name_zh || '-'}
                        </div>
                        <div className="text-xs text-gray-500">
                          還：{order.return_location?.name_zh || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        NT$ {formatCurrency(cost.finalTotal)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full whitespace-nowrap ${getStatusColor(order.status)}`}>
                            {getStatusText(order.status)}
                          </span>
                          {getStatePhaseBadge(order.status, order.end_date)}
                          {order.status === 'approved' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                              <i className="ri-information-line text-xs"></i>非備妥
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewOrder(order.id);
                          }}
                          className="text-blue-600 hover:text-blue-800 font-medium"
                        >
                          查看詳情
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredOrders.length === 0 && (
            <div className="text-center py-12">
              <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500">沒有找到符合條件的訂單</p>
            </div>
          )}

          {/* 分頁控制列 */}
          {filteredOrders.length > 0 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-600">
                共 <span className="font-semibold">{filteredOrders.length}</span> 筆，顯示第{' '}
                <span className="font-semibold">{startIdx + 1}</span> –{' '}
                <span className="font-semibold">{Math.min(endIdx, filteredOrders.length)}</span> 筆
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {getPageNumbers().map((p, idx) =>
                  typeof p === 'string' ? (
                    <span key={`dot-${idx}`} className="px-2 text-gray-400 text-sm">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`px-3 py-1.5 text-sm rounded-lg border cursor-pointer whitespace-nowrap ${
                        p === safePage
                          ? 'bg-teal-600 text-white border-teal-600'
                          : 'border-gray-300 bg-white hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}